import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  Building2,
  Landmark,
  Lock,
  Pencil,
  Plus,
  Trash2,
  Undo2,
  Wallet,
} from 'lucide-react';
import {
  BANK_ACCOUNT_KIND_LABELS,
  formatCents,
  type BankAccount,
  type BankAccountKind,
  type BankTransfer,
  type Business,
  type Company,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { blank, formatDate } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ActiveBadge, StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { todayISO, useBusinesses } from '@/lib/queries';
import { DateInput } from '@/components/DateInput';

/**
 * Contas bancárias e caixas.
 *
 * O saldo mostrado é saldo de abertura + baixas já líquidas de estorno: não
 * existe "saldo digitado". Quem quiser mudar o saldo dá baixa ou estorna.
 */
export function ContasPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<BankAccount | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reversing, setReversing] = useState<BankTransfer | null>(null);
  const [editingBusiness, setEditingBusiness] = useState<Business | 'new' | null>(null);

  const businessesQuery = useBusinesses(true);

  const listQuery = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<{ data: BankAccount[] }>('/bank-accounts'),
  });

  const transfersQuery = useQuery({
    queryKey: ['bank-transfers'],
    queryFn: () => api.get<{ data: BankTransfer[] }>('/bank-transfers'),
  });

  const companyQuery = useQuery({
    queryKey: ['company'],
    queryFn: () => api.get<Company>('/company'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['bank-transfers'] });
    void queryClient.invalidateQueries({ queryKey: ['company'] });
    void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  };

  const reverseMutation = useMutation({
    mutationFn: (payload: { id: string; reason: string }) =>
      api.post<BankTransfer>(`/bank-transfers/${payload.id}/reverse`, { reason: payload.reason }),
    onSuccess: () => {
      setReversing(null);
      refresh();
    },
  });

  const closedUntil = companyQuery.data?.financialClosedUntil ?? null;

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/bank-accounts/${id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const rows = listQuery.data?.data ?? [];
  const totalCents = rows
    .filter((row) => row.isActive)
    .reduce((acc, row) => acc + row.currentBalanceCents, 0);

  const columns: Array<Column<BankAccount>> = [
    {
      key: 'name',
      label: 'Conta',
      strong: true,
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {row.kind === 'CAIXA' ? (
            <Wallet size={14} color={T.inkFaint} aria-hidden />
          ) : (
            <Landmark size={14} color={T.inkFaint} aria-hidden />
          )}
          {row.name}
        </span>
      ),
    },
    { key: 'businessName', label: 'Empresa', render: (row) => blank(row.businessName) },
    { key: 'kind', label: 'Tipo', render: (row) => BANK_ACCOUNT_KIND_LABELS[row.kind] },
    { key: 'bank', label: 'Banco', render: (row) => blank(row.bank) },
    {
      key: 'agency',
      label: 'Agência / conta',
      mono: true,
      render: (row) =>
        row.agency || row.accountNumber ? `${row.agency ?? '—'} / ${row.accountNumber ?? '—'}` : '—',
    },
    {
      key: 'openingBalanceCents',
      label: 'Abertura',
      right: true,
      mono: true,
      render: (row) => formatCents(row.openingBalanceCents),
    },
    {
      key: 'currentBalanceCents',
      label: 'Saldo atual',
      right: true,
      mono: true,
      strong: true,
      render: (row) => (
        <span style={{ color: row.currentBalanceCents < 0 ? T.red : undefined }}>
          {formatCents(row.currentBalanceCents)}
        </span>
      ),
    },
    { key: 'isActive', label: 'Situação', render: (row) => <ActiveBadge active={row.isActive} /> },
    {
      key: 'actions',
      label: '',
      width: 96,
      right: true,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 4 }} onClick={(event) => event.stopPropagation()}>
          {can('contas-bancarias:editar') && (
            <Button small variant="ghost" icon={Pencil} onClick={() => setEditing(row)} aria-label={`Editar ${row.name}`} />
          )}
          {can('contas-bancarias:excluir') && (
            <Button small variant="ghost" icon={Trash2} onClick={() => setRemoving(row)} aria-label={`Excluir ${row.name}`} />
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Contas e caixas"
        meta={[
          { label: 'Saldo das contas ativas', value: formatCents(totalCents) },
        ]}
        actions={
          <span style={{ display: 'inline-flex', gap: 8 }}>
            {can('lancamentos:aprovar') && (
              <Button icon={Lock} onClick={() => setClosing(true)}>
                {closedUntil ? `Fechado até ${formatDate(closedUntil)}` : 'Fechar período'}
              </Button>
            )}
            {can('contas-bancarias:editar') && (
              <Button icon={ArrowLeftRight} onClick={() => setTransferring(true)}>
                Transferir
              </Button>
            )}
            {can('contas-bancarias:criar') && (
              <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                Nova conta
              </Button>
            )}
          </span>
        }
      />

      <div className="workspace__body">
        {/* Empresas do grupo: cada banco abaixo é de uma delas, e cada conta a
            pagar só se baixa num banco da própria empresa. */}
        <section aria-label="Empresas do grupo" style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 10,
            }}
          >
            <h2 style={{ fontSize: 15, margin: 0 }}>Empresas do grupo</h2>
            {can('empresas-financeiro:criar') && (
              <Button small icon={Plus} onClick={() => setEditingBusiness('new')}>
                Nova empresa
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(businessesQuery.data ?? []).map((business) => (
              <button
                key={business.id}
                type="button"
                className="filterbar__chip"
                disabled={!can('empresas-financeiro:editar')}
                onClick={() => setEditingBusiness(business)}
                aria-label={`Editar ${business.name}`}
                style={{ opacity: business.isActive ? 1 : 0.55 }}
              >
                <Building2 size={14} aria-hidden />
                {business.name}
                {business.isDefault && <StatusBadge kind="info">Padrão</StatusBadge>}
                {!business.isActive && <StatusBadge kind="mute">Desativada</StatusBadge>}
              </button>
            ))}
          </div>
        </section>

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Contas bancárias e caixas"
            columns={columns}
            rows={rows}
            emptyState={
              <EmptyState
                title="Nenhuma conta cadastrada"
                description="Cadastre a conta da obra para conseguir dar baixa nos pagamentos."
                action={
                  can('contas-bancarias:criar') ? (
                    <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                      Nova conta
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}

        <h2 style={{ fontSize: 15, margin: '24px 0 12px' }}>Transferências entre contas</h2>
        {transfersQuery.data && (
          (transfersQuery.data.data.length === 0 ? (
            <p className="muted" style={{ fontSize: 13 }}>
              Nenhuma transferência ainda. Use "Transferir" para mover dinheiro entre caixa e banco —
              sem passar por receita nem despesa.
            </p>
          ) : (
            <SmartDataTable
              caption="Transferências entre contas"
              columns={[
                { key: 'code', label: 'Código', mono: true, strong: true, render: (row: BankTransfer) => row.code },
                { key: 'date', label: 'Data', mono: true, render: (row: BankTransfer) => formatDate(row.transferredAt) },
                {
                  key: 'flow',
                  label: 'De → Para',
                  render: (row: BankTransfer) => `${row.fromAccountName ?? '—'} → ${row.toAccountName ?? '—'}`,
                },
                {
                  key: 'amount',
                  label: 'Valor',
                  right: true,
                  mono: true,
                  strong: true,
                  render: (row: BankTransfer) => formatCents(row.amountCents),
                },
                { key: 'by', label: 'Por', render: (row: BankTransfer) => blank(row.createdByName) },
                {
                  key: 'status',
                  label: '',
                  render: (row: BankTransfer) =>
                    row.isReversal ? (
                      <StatusBadge kind="warn">Estorno</StatusBadge>
                    ) : row.reason ? null : null,
                },
                {
                  key: 'actions',
                  label: '',
                  width: 112,
                  right: true,
                  render: (row: BankTransfer) =>
                    !row.isReversal && can('contas-bancarias:editar') ? (
                      // Estorno mexe em saldo: a ação diz o nome dela, não fica
                      // escondida atrás de uma setinha.
                      <Button
                        small
                        variant="ghost"
                        icon={Undo2}
                        aria-label={`Estornar ${row.code}`}
                        onClick={() => setReversing(row)}
                      >
                        Estornar
                      </Button>
                    ) : null,
                },
              ]}
              rows={transfersQuery.data.data}
            />
          ))
        )}
      </div>

      <TransferModal
        open={transferring}
        accounts={rows.filter((row) => row.isActive)}
        onClose={() => setTransferring(false)}
        onDone={() => {
          setTransferring(false);
          refresh();
        }}
      />

      <ClosePeriodModal
        open={closing}
        closedUntil={closedUntil}
        onClose={() => setClosing(false)}
        onDone={() => {
          setClosing(false);
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(reversing)}
        destructive
        requireReason
        loading={reverseMutation.isPending}
        title={reversing ? `Estornar ${reversing.code}?` : ''}
        confirmLabel="Estornar transferência"
        message={
          <>
            Cria a contra-transferência na MESMA data, devolvendo o dinheiro à conta
            de origem. Nada é apagado — as duas linhas ficam no histórico.
            {reverseMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(reverseMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setReversing(null);
          reverseMutation.reset();
        }}
        onConfirm={(reason) =>
          reversing && reverseMutation.mutate({ id: reversing.id, reason: reason ?? '' })
        }
      />

      <BusinessForm
        open={editingBusiness !== null}
        business={editingBusiness === 'new' ? null : editingBusiness}
        onClose={() => setEditingBusiness(null)}
        onSaved={() => {
          setEditingBusiness(null);
          refresh();
        }}
      />

      <BankAccountForm
        open={creating || Boolean(editing)}
        account={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        destructive
        loading={removeMutation.isPending}
        title="Excluir esta conta?"
        confirmLabel="Excluir"
        message={
          <>
            Só é possível excluir uma conta que nunca recebeu baixa. Se já houve
            movimento, deixe-a inativa — o histórico precisa continuar de pé.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}

function TransferModal({
  open,
  accounts,
  onClose,
  onDone,
}: {
  open: boolean;
  accounts: BankAccount[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [fromAccountId, setFromAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [transferredAt, setTransferredAt] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setFromAccountId('');
      setToAccountId('');
      setAmountCents(null);
      setTransferredAt(todayISO());
      setNotes('');
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<BankTransfer>('/bank-transfers', body),
    onSuccess: onDone,
  });

  const errors = fieldErrors(mutation.error);
  const from = accounts.find((account) => account.id === fromAccountId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Transferir entre contas"
      description="Dinheiro mudando de bolso — não vira receita nem despesa"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={ArrowLeftRight}
            loading={mutation.isPending}
            disabled={!fromAccountId || !toAccountId || fromAccountId === toAccountId || !amountCents}
            onClick={() =>
              mutation.mutate({
                fromAccountId,
                toAccountId,
                amountCents,
                transferredAt,
                notes: notes.trim() || null,
              })
            }
          >
            Transferir
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <div className="grid-2">
          <Field label="Sai de" htmlFor="tr-from" error={errors.fromAccountId}>
            <select
              id="tr-from"
              className="input"
              value={fromAccountId}
              onChange={(event) => setFromAccountId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} · {formatCents(account.currentBalanceCents)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Entra em" htmlFor="tr-to" error={errors.toAccountId}>
            <select
              id="tr-to"
              className="input"
              value={toAccountId}
              onChange={(event) => setToAccountId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {accounts
                .filter((account) => account.id !== fromAccountId)
                .map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
            </select>
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Valor" htmlFor="tr-amount" error={errors.amountCents}>
            <MoneyInput id="tr-amount" value={amountCents} onChange={setAmountCents} />
          </Field>
          <Field label="Data" htmlFor="tr-date" error={errors.transferredAt}>
            <DateInput
              id="tr-date"
              className="input input--mono"
              value={transferredAt}
              onChange={(iso) => setTransferredAt(iso)}
            />
          </Field>
        </div>
        {from && amountCents !== null && amountCents > from.currentBalanceCents && (
          <p style={{ color: T.amber, fontSize: 12.5 }}>
            Atenção: o valor passa do saldo atual de {from.name} ({formatCents(from.currentBalanceCents)}).
          </p>
        )}
        <Field label="Observação" htmlFor="tr-notes">
          <input
            id="tr-notes"
            className="input"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Sangria do caixa para o banco"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}

function ClosePeriodModal({
  open,
  closedUntil,
  onClose,
  onDone,
}: {
  open: boolean;
  closedUntil: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [until, setUntil] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setUntil(closedUntil ? closedUntil.slice(0, 10) : '');
  }

  const mutation = useMutation({
    mutationFn: (value: string | null) =>
      api.post('/financial/close-period', { until: value }),
    onSuccess: onDone,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Fechamento de período"
      description="Ninguém lança, baixa ou estorna com data dentro do período fechado"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          {closedUntil && (
            <Button
              variant="danger"
              loading={mutation.isPending}
              onClick={() => mutation.mutate(null)}
            >
              Reabrir tudo
            </Button>
          )}
          <Button
            variant="primary"
            icon={Lock}
            loading={mutation.isPending}
            disabled={!until}
            onClick={() => mutation.mutate(until)}
          >
            Fechar até esta data
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field
          label="Fechar o financeiro até (inclusive)"
          htmlFor="close-until"
          hint="O normal é o último dia do mês encerrado. Reabrir também fica na auditoria."
        >
          <DateInput
            id="close-until"
            className="input input--mono"
            value={until}
            onChange={(iso) => setUntil(iso)}
          />
        </Field>
        {closedUntil && (
          <p className="muted" style={{ fontSize: 12.5 }}>
            Hoje o financeiro está fechado até <strong>{formatDate(closedUntil)}</strong>.
          </p>
        )}
      </FormSection>
    </Modal>
  );
}

function BankAccountForm({
  open,
  account,
  onClose,
  onSaved,
}: {
  open: boolean;
  account: BankAccount | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const businesses = useBusinesses();
  const [businessId, setBusinessId] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<BankAccountKind>('BANCO');
  const [bank, setBank] = useState('');
  const [agency, setAgency] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [openingBalanceCents, setOpeningBalanceCents] = useState<number | null>(0);
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (account?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setBusinessId(account?.businessId ?? '');
    setName(account?.name ?? '');
    setKind(account?.kind ?? 'BANCO');
    setBank(account?.bank ?? '');
    setAgency(account?.agency ?? '');
    setAccountNumber(account?.accountNumber ?? '');
    setOpeningBalanceCents(account?.openingBalanceCents ?? 0);
    setIsActive(account?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      account ? api.patch(`/bank-accounts/${account.id}`, body) : api.post('/bank-accounts', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  // Uma empresa só: vem marcada. Mais de uma: o operador escolhe.
  const empresas = businesses.data ?? [];
  const empresa = businessId || (empresas.length === 1 ? empresas[0]!.id : '');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={account ? 'Editar conta' : 'Nova conta'}
      description="O saldo atual é calculado pelas baixas — aqui você informa só a abertura"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={name.trim().length < 2 || !empresa}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                businessId: empresa,
                name: name.trim(),
                kind,
                bank: bank.trim() || null,
                agency: agency.trim() || null,
                accountNumber: accountNumber.trim() || null,
                openingBalanceCents: openingBalanceCents ?? 0,
                isActive,
              })
            }
          >
            {account ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field
          label="Empresa"
          htmlFor="bank-business"
          error={errors.businessId}
          hint="Esta conta só baixa lançamentos desta empresa"
        >
          <select
            id="bank-business"
            className="input"
            value={empresa}
            onChange={(event) => setBusinessId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {empresas.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Nome da conta" htmlFor="bank-name" error={errors.name}>
          <input
            id="bank-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Conta obra — Banco do Brasil"
          />
        </Field>

        <div className="grid-2">
          <Field label="Tipo" htmlFor="bank-kind">
            <select
              id="bank-kind"
              className="input"
              value={kind}
              onChange={(event) => setKind(event.target.value as BankAccountKind)}
            >
              {Object.entries(BANK_ACCOUNT_KIND_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Banco" htmlFor="bank-bank">
            <input
              id="bank-bank"
              className="input"
              value={bank}
              onChange={(event) => setBank(event.target.value)}
              disabled={kind === 'CAIXA'}
            />
          </Field>
        </div>

        <div className="grid-2">
          <Field label="Agência" htmlFor="bank-agency">
            <input
              id="bank-agency"
              className="input input--mono"
              value={agency}
              onChange={(event) => setAgency(event.target.value)}
              disabled={kind === 'CAIXA'}
            />
          </Field>
          <Field label="Conta" htmlFor="bank-number">
            <input
              id="bank-number"
              className="input input--mono"
              value={accountNumber}
              onChange={(event) => setAccountNumber(event.target.value)}
              disabled={kind === 'CAIXA'}
            />
          </Field>
        </div>

        <Field
          label="Saldo de abertura"
          htmlFor="bank-opening"
          error={errors.openingBalanceCents}
          hint="Saldo no dia em que esta conta entrou no sistema"
        >
          <MoneyInput id="bank-opening" value={openingBalanceCents} onChange={setOpeningBalanceCents} />
        </Field>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          Conta ativa
        </label>
      </FormSection>
    </Modal>
  );
}

/** Cadastro de uma empresa do grupo — nome, CNPJ, padrão e ativa. */
function BusinessForm({
  open,
  business,
  onClose,
  onSaved,
}: {
  open: boolean;
  business: Business | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [document, setDocument] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (business?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setName(business?.name ?? '');
    setDocument(business?.document ?? '');
    setIsDefault(business?.isDefault ?? false);
    setIsActive(business?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      business ? api.patch(`/businesses/${business.id}`, body) : api.post('/businesses', body),
    onSuccess: onSaved,
  });
  const errors = fieldErrors(mutation.error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={business ? `Editar ${business.name}` : 'Nova empresa'}
      description="Cada conta a pagar e cada banco são de uma empresa — é o recorte da DRE"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={name.trim().length < 2}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                name: name.trim(),
                document: document.trim() || null,
                isDefault,
                isActive,
              })
            }
          >
            {business ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Nome da empresa" htmlFor="business-name" error={errors.name}>
          <input
            id="business-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="BELLA CENTER"
          />
        </Field>
        <Field label="CNPJ" htmlFor="business-document" error={errors.document} hint="Opcional">
          <input
            id="business-document"
            className="input input--mono"
            value={document}
            onChange={(event) => setDocument(event.target.value)}
            placeholder="00.000.000/0000-00"
          />
        </Field>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={isDefault}
            disabled={business?.isDefault}
            onChange={(event) => setIsDefault(event.target.checked)}
          />
          Empresa padrão (recebe o que Compras, RH e Clube lançam sozinhos)
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isActive}
            disabled={business?.isDefault}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Empresa ativa
        </label>
      </FormSection>
    </Modal>
  );
}
